import { useMemo, useRef } from 'react';
import { t } from '@lingui/macro';
import _, { merge } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useDataService } from '../../../services/ApiDataService';
import { convertLoadParamsToQueryParams, convertQueryResponse, useQueryLoader } from '../../data-table/Loaders';
import FormSection from '../../form/FormSection';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import {
    ACCESS_RULES,
    USER_ACCESS_ENTITY,
    SYSTEM_ADMIN_ROLE,
    USER_ACCESS_PLM_DESCRIPTION,
    USER_ACCESS_PLM_ID,
    USER_ACCESS_PLM_NAME,
    USER_ROLE_FIELD,
    USER_ACCESS_PLM_EDITABLE,
} from './UserConstants';
import { ID_FIELD } from '../../form/FormConstants';
import { useUser } from '../../../stores/SessionStore';
import { useIsPgm } from '../../../stores/EnvironmentStore';
import DataTable from '../../data-table/DataTable';
import ListSelectorField from '../../form/ListSelectorField';
import { usePlmId } from '../../../utilities/PgmUtilities';
import { PLM_PAGE_TITLE } from '../plms/PlmConstants';

const defaultVisibleRows = 12;
const selectionDataSource = {
    totalProperty: 'total',
    dataProperty: 'data',
    params: {
        properties: [USER_ACCESS_PLM_NAME, USER_ACCESS_PLM_DESCRIPTION],
    },
    url: '/api/v3/plms/selection',
    initialSort: [{ property: USER_ACCESS_PLM_NAME, direction: SORT_ASC }],
};

const initialSort = [{ property: USER_ACCESS_PLM_NAME, direction: SORT_ASC }];

const addButton = t`Add Local Managers`;
const emptyGridText = t`This user does not have access to any Local Managers`;

function buildUserAccessColumns() {
    return [
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Name`,
            sortable: true,
            mapping: USER_ACCESS_PLM_NAME,
            width: 250,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Description`,
            sortable: false,
            mapping: USER_ACCESS_PLM_DESCRIPTION,
            width: 400,
        },
    ];
}

function convertIds(dataSource, response, excludes) {
    const ret = convertQueryResponse(dataSource, response);
    return _.extend(ret, {
        rows: _(ret)
            .get('rows', [])
            .map((rec) => {
                const _id = _.get(rec, USER_ACCESS_PLM_ID);
                if (_.findIndex(excludes, (it) => it === _id) !== -1) {
                    return rec;
                }
                return _.extend(rec, { _id });
            }),
    });
}

function queryFn(loader, dataService, excludes, dataSource, ids) {
    return loader(() => {
        if (!dataSource) {
            return null;
        }
        return (loaderParams) => {
            const { url, params: datasourceParams } = dataSource;
            const queryParams = merge({}, datasourceParams, convertLoadParamsToQueryParams(loaderParams));
            const body = _.extend({}, _.get(dataSource, 'defaultBody', {}), { selection: ids });
            return dataService
                .post(url, body, queryParams)
                .then((response) => convertIds(dataSource, response, excludes));
        };
    }, [dataService, dataSource, ids]);
}

function isSuper(userRole) {
    return userRole === SYSTEM_ADMIN_ROLE;
}

function accessRecord(entity, restricted, type = 'plm') {
    const editable = _.findIndex(restricted, (it) => it === entity) === -1;
    return { entity, type, editable };
}

function accessRecords(selectedIds, restricted) {
    return _(selectedIds)
        .map((id) => accessRecord(id, restricted))
        .value();
}

export default function PlmUserAccess({ formData, setSelectionIsDirty }) {
    const columns = buildUserAccessColumns();
    const dataService = useDataService();
    const loader = useMemo;
    const selected = useRef([]);
    const [isPgm] = useIsPgm();
    const plmId = usePlmId();
    const [loggedInUser] = useUser();
    const { setValue, watch } = useFormContext();
    const userRole = watch(USER_ROLE_FIELD);
    const rules = watch(ACCESS_RULES);

    const restricted = _(rules)
        .filter((x) => !_.get(x, USER_ACCESS_PLM_EDITABLE, false))
        .map((x) => _.get(x, USER_ACCESS_ENTITY))
        .value();
    selected.current = _(rules)
        .map((x) => _.get(x, USER_ACCESS_ENTITY))
        .value();

    const updateSelection = (selectedIds) => {
        selected.current = selectedIds;
        setValue(ACCESS_RULES, isSuper(userRole) ? null : accessRecords(selectedIds, restricted));

        if (_.isFunction(setSelectionIsDirty)) {
            setSelectionIsDirty(true);
        }
    };

    function resolveInverted(invertedIds) {
        const lookup = {
            ...selectionDataSource,
            url: '/api/v3/plms/selection',
        };
        const { url, params: datasourceParams } = lookup;
        const queryParams = merge({}, datasourceParams);
        const body = _.extend({}, { inverted: true, selection: invertedIds });
        return dataService
            .post(url, body, queryParams)
            .then((response) => convertIds(lookup, response, restricted))
            .then((response) => _.map(response?.rows || [], (i) => i?.id));
    }

    const availableDataSource = {
        ...selectionDataSource,
        defaultBody: { inverted: true, userAccess: true },
    };

    const selectedDataSource = {
        ...selectionDataSource,
        url: '/api/v3/plms/selection',
        defaultBody: { inverted: false },
    };
    const selfDataSource = {
        ...selectionDataSource,
        url: '/api/v3/plms/list',
    };

    const selectedLoaderFn = _.partial(queryFn, loader, dataService, restricted);
    const availableLoaderFn = _.partial(queryFn, loader, dataService, []);

    const tableColumns = [...columns];

    const roLoader = useQueryLoader(selfDataSource, { disableFilterState: true });
    if (!isPgm || !_.isEmpty(plmId) || isSuper(userRole)) {
        return <span />;
    }

    if (_.get(formData, ID_FIELD) === _.get(loggedInUser, ID_FIELD)) {
        return (
            <div title={PLM_PAGE_TITLE}>
                <DataTable
                    loader={roLoader}
                    columns={columns}
                    disableCSVExport
                    disableColumnRemover
                    visibleRows={defaultVisibleRows}
                />
            </div>
        );
    }

    return (
        <FormSection title={PLM_PAGE_TITLE}>
            <ListSelectorField
                gridColumns={tableColumns}
                selectedGridColumns={columns}
                sorters={initialSort}
                dbSelected={selected.current}
                fieldName={ACCESS_RULES}
                selectedDataSource={selectedDataSource}
                selectedLoaderFunction={selectedLoaderFn}
                availableDataSource={availableDataSource}
                availableLoaderFunction={availableLoaderFn}
                addButtonText={addButton}
                setSelectionIsDirty={setSelectionIsDirty}
                isMultiRemove={false}
                selectedEmptyGridText={emptyGridText}
                updateSelectionFunction={updateSelection}
                resolveInvertedFunction={resolveInverted}
                availableSelectAll
            />
        </FormSection>
    );
}
